import { useEffect } from 'react';
import { 
  initTechnicalSEO,
  autoFixTechnicalSEO
} from '@/utils/technicalSEO';
import { 
  performSEOAudit,
  autoFixSEOIssues 
} from '@/utils/seoAudit';

/**
 * Ultimate Auto-SEO Hook - Just call this and everything is handled
 */
export const useAutoSEO = (options: {
  autoFix?: boolean;
  trackAnalytics?: boolean;
  logResults?: boolean;
} = {}) => {
  const {
    autoFix = true,
    trackAnalytics = true,
    logResults = process.env.NODE_ENV === 'development'
  } = options;

  useEffect(() => {
    const runAutoSEO = async () => {
      if (logResults) console.log('🚀 Auto-SEO initializing...');
      
      try {
        // 1. Auto-fix all technical SEO issues
        if (autoFix) {
          const fixes = autoFixSEOIssues();
          if (fixes.length > 0 && logResults) {
            console.log('✅ Auto-fixed SEO issues:', fixes);
          }
        }
        
        // 2. Initialize technical SEO systems
        initTechnicalSEO({
          enforceHTTPS: true,
          securityHeaders: true,
          duplicateContentPrevention: true,
          crawlBudgetOptimization: true,
          canonicalURLs: false,
          robotsOptimization: true,
        });
        
        // 4. Run SEO audit
        const auditResult = performSEOAudit();
        
        if (logResults) {
          console.log(`🎯 SEO Score: ${auditResult.score}/100`);
          
          if (auditResult.issues.length > 0) {
            console.warn('⚠️ SEO Issues:', auditResult.issues);
          }
          
          if (auditResult.recommendations.length > 0) {
            console.info('💡 SEO Recommendations:', auditResult.recommendations);
          }
        }
        
        // 5. Track analytics
        if (trackAnalytics && typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'auto_seo_complete', {
            event_category: 'SEO',
            event_label: 'Auto-SEO Initialization',
            seo_score: auditResult.score,
            issues_found: auditResult.issues.length,
          });
        }
        
        if (logResults) console.log('✨ Auto-SEO complete!');
        
      } catch (error) {
        console.error('❌ Auto-SEO error:', error);
      }
    };

    // Run when DOM is ready
    if (document.readyState === 'complete') {
      runAutoSEO();
    } else {
      window.addEventListener('load', runAutoSEO);
      return () => window.removeEventListener('load', runAutoSEO);
    }
  }, [autoFix, trackAnalytics, logResults]);

  // Auto-fix issues on route changes
  useEffect(() => {
    const handleRouteChange = () => {
      if (autoFix) {
        setTimeout(() => {
          const fixes = autoFixSEOIssues();
          if (fixes.length > 0 && logResults) {
            console.log('🔄 Route change - Auto-fixed:', fixes);
          }
        }, 100);
      }
    };

    // Listen for route changes (works with most SPA routers)
    window.addEventListener('popstate', handleRouteChange);
    
    // Also listen for pushstate/replacestate (for programmatic navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    
    history.pushState = function(...args) {
      originalPushState.apply(history, args);
      handleRouteChange();
    };
    
    history.replaceState = function(...args) {
      originalReplaceState.apply(history, args);
      handleRouteChange();
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [autoFix, logResults]);
};
